import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Body2,
  Button,
  CancelCrossIcon,
  Caption1,
  Dialog,
  Header2,
  HelpIcon,
  IconButton,
  InvoicesIcon,
  ListItem,
  ListSeparator,
  Step,
  WarningTriangleIcon,
  Wizard,
} from "@activecollab/components";
import classnames from "classnames";
import BuyNowButton from "~/components/BuyNowButton/BuyNowButton";
import { useLinkContext } from "~/components/LinkProvider/context";
import ActivateFreePlanDialog from "~/components/Subscription/ActivateFreePlanDialog";
import AddPaymentMethodProcess from "~/components/Subscription/AddPaymentMethodProcess";
import AddStripeCardDialog from "~/components/Subscription/BillingPaymentMethod/AddStripeCardDialog";
import BlockingPurchaseDialog from "~/components/Subscription/BlockingPurchaseDialog";
import CancelSurvey from "~/components/Subscription/CancelSurvey";
import CompletingProcessSuccessDialog from "~/components/Subscription/CompletingProcessSuccessDialog";
import OrderSummaryDialog from "~/components/Subscription/OrderSummaryDialog";
import CancellationProcessSecondStep from "~/components/Subscription/SubscriptionPlanDetails/CancellationFlowSecondStep";
import LimitedPlanDetails from "~/components/Subscription/SubscriptionPlanDetails/LimitedPlanDetails";
import PerSeatPlanDetails from "~/components/Subscription/SubscriptionPlanDetails/PerSeatPlanDetails";
import PlanDetails from "~/components/Subscription/SubscriptionPlanDetails/PlanDetails";
import { showErrorToast, showSuccessToast } from "~/components/Toast";
import * as actionTypes from "~/store/action_types";
import { fetchAccountSettings } from "~/store/actions/actions.accountSettings";
import { completeBillingOrder, editBillingOrder } from "~/store/actions/actions.billingOrder";
import { getAccountStatsBeforeCancellation } from "~/store/actions/actions.cancelSubscription";
import { fetchAllInvoices, fetchBrakeDownPrices } from "~/store/actions/actions.subscription";
import { fetchRecordedBalance } from "~/store/actions/actions.subscriptionBalance";
import { fetchPlans, switchIsMonthly, switchToPlan } from "~/store/actions/actions.subscriptionPlans";
import { getAmountAvailable } from "~/store/selectors/selector.accountBalance";
import {
  getAccountPlanInfo,
  getBillingPeriod,
  getCurrentPlanName,
  getIsTrial,
  getStorageUsed,
  getSubscriptionFooterText,
  isAddOnEnabled,
  isLifetimePricingModel,
  isPerSeatPricingModel,
  getIsCanceled,
  getIsAccountSuspended,
  getIsFailedPayment,
  getIsSuspendedTrial,
  isLegacyPricingModel,
  getIsAccountActived,
} from "~/store/selectors/selector.accountSettings";
import { getCancelSubscriptionAccountStats } from "~/store/selectors/selector.cancelSubscriptionAccountStats";
import { getFeatureFlag } from "~/store/selectors/selector.featureFlags";
import { getAllSubscriptionPlans } from "~/store/selectors/selector.subscriptionPlans";
import Angie from "~/utils/angie";

const SubscriptionPlanDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { liveSupportQueryData } = useLinkContext();

  const queryParams = liveSupportQueryData ? `?${liveSupportQueryData}` : "";
  const helpUrl = `https://activecollab.com/help/books/leaving-ac/leaving${queryParams}`;

  const isTrial = useSelector((state) => getIsTrial(state));
  const isLifetime = useSelector((state) => isLifetimePricingModel(state));
  const isPerSeat = useSelector((state) => isPerSeatPricingModel(state));
  const isLegacyPricing = useSelector((state) => isLegacyPricingModel(state));
  const isCanceled = useSelector(getIsCanceled);
  const isActivated = useSelector(getIsAccountActived);
  const isSuspended = useSelector(getIsAccountSuspended);
  const isFailedPayment = useSelector(getIsFailedPayment);
  const accountPlanInfo = useSelector((state) => getAccountPlanInfo(state));
  const storageUsage = useSelector((state) => getStorageUsed(state));
  const balanceTotal = useSelector((state) => getAmountAvailable(state));
  const currentBillingPeriod = useSelector((state) => getBillingPeriod(state));
  const currentPlan = useSelector((state) => getCurrentPlanName(state));
  const cancelSubscriptionLinkText = useSelector((state) => getSubscriptionFooterText(state));
  const isSuspendedTrial = useSelector((state) => getIsSuspendedTrial(state));
  const isFreePlanSurveyAvailable = useSelector((state) => getFeatureFlag(state, "free_activation_survey"));
  const isFreePlanAvailableForCancellation = useSelector((state) => getFeatureFlag(state, "free_plan_in_cancellation"));
  const isFreePlanAvailableForActivation = useSelector((state) => getFeatureFlag(state, "free_plan_activation"));
  const [showAddPaymentMethodProcess, setAddPaymentMethodProcess] = useState(false);
  const [showUpdateCreditCard, setUpdateCreditCard] = useState(false);
  const plans = useSelector((state) => getAllSubscriptionPlans(state));
  const freePlan = plans
    .toList()
    .filter((plan) => plan.get(`name`)?.toLowerCase() === "free")
    .map((plan) => plan.toJS())
    .first();
  const [isMonthly, setIsMonthly] = useState(currentBillingPeriod === "monthly");
  const [reloadOnAction, setReloadOnAction] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showBuyingProcessSuccess, setShowBuyingProcessSuccess] = useState(false);
  const [buyingProcessSuccessPlan, setBuyingProcessSuccessPlan] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showCancellationWizard, setShowCancellationWizard] = useState(false);
  const [isBlockingDialogOpen, setIsBlockingDialogOpen] = useState(false);

  const isCurrentPlanFreePlan = currentPlan ? currentPlan.toLowerCase() === "free" : false;

  const isCurrentPlanAppSumoPlan = currentPlan ? currentPlan.toLowerCase().includes("app_sumo") : null;

  const isClassic = isLegacyPricing && currentPlan?.toLowerCase() !== "plus_plan";

  const [showActivateConfirmationModal, setShowActivateConfirmationModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(getAccountStatsBeforeCancellation());
  }, [dispatch]);

  const accountStats = useSelector(getCancelSubscriptionAccountStats);
  const closeActivateDialog = useCallback(() => {
    setShowActivateConfirmationModal(false);
  }, []);
  const closeBlockingDialog = useCallback(() => {
    setIsBlockingDialogOpen(false);
  }, []);

  const isGetPaidFullEnabled = useSelector((state) => isAddOnEnabled(state, "get_paid_full"));
  const isGetPaidEnabled = useSelector((state) => isAddOnEnabled(state, "get_paid"));

  const submitConfirmationModal = useCallback(() => {
    return isActivated && (isFailedPayment || isSuspended)
      ? setIsBlockingDialogOpen(true)
      : setShowConfirmationModal(true);
  }, [isActivated, isFailedPayment, isSuspended]);
  const closeConfirmationModal = useCallback(() => {
    setIsDialogOpen(false);
    setShowConfirmationModal(false);
  }, []);

  const onUpdateCreditCard = useCallback(() => {
    showSuccessToast("Payment details updated.");
    setUpdateCreditCard(false);
    setShowActivateConfirmationModal(true);
  }, []);

  const onCancelUpdateCreditCard = useCallback(() => {
    setUpdateCreditCard(false);
  }, []);

  const openUpdateCreditCard = useCallback(() => {
    closeActivateDialog();
    setUpdateCreditCard(true);
  }, [closeActivateDialog]);

  const closeWizard = useCallback(() => {
    setAddPaymentMethodProcess(false);
  }, []);
  const onSubmitWizard = useCallback(() => {
    setAddPaymentMethodProcess(false);
    setShowActivateConfirmationModal(true);
  }, []);
  const openWizard = useCallback(() => {
    closeActivateDialog();
    setAddPaymentMethodProcess(true);
  }, [closeActivateDialog]);

  const switchToAnnually = useCallback(
    (addOns = []) =>
      () => {
        dispatch(switchToPlan(currentPlan, false, addOns)).then((result) => {
          const { response = { entities: {}, result: {} } } = result;
          const {
            entities: { billingOrder },
            result: { single },
          } = response;

          if (
            billingOrder &&
            typeof billingOrder[single] === `object` &&
            result.type === actionTypes.SWITCH_TO_SUBSCRIPTION_PLAN_SUCCESS
          ) {
            return setShowActivateConfirmationModal(true);
          }

          if (result.type === actionTypes.SWITCH_TO_SUBSCRIPTION_PLAN_ERROR) {
            Angie.logger.error("Failed to create billing order.", {
              error: result.error,
            });

            return showErrorToast("Unable to switch to annual");
          }

          return showErrorToast("Unable to switch to annual");
        });
      },
    [currentPlan, dispatch]
  );

  const onSubmit = useCallback(() => {
    setIsLoading(true);
    dispatch(completeBillingOrder()).then((result) => {
      if (result.response.next_action_url) {
        window.location.href = result.response.next_action_url;

        return window.location.href;
      }

      if (result.error) {
        setIsLoading(false);
        dispatch(editBillingOrder());
        showErrorToast(result.response.message);
        Angie.logger.error("Unable switch to annual.", {
          error: result.error,
        });

        return showErrorToast(result.response.message);
      }

      if (result.response.is_paid) {
        setIsLoading(false);
        dispatch(fetchAccountSettings());
        dispatch(fetchRecordedBalance());
        dispatch(fetchAllInvoices());
        setShowActivateConfirmationModal(false);

        return showSuccessToast("Account upgraded.");
      }

      setIsLoading(false);

      return showErrorToast("Sorry, we cannot upgrade your account");
    });
  }, [dispatch]);

  const handleChoosePlan = useCallback(() => {
    navigate("/subscription/choose-plan");
  }, [navigate]);

  useEffect(() => {
    dispatch(fetchPlans());
    dispatch(fetchBrakeDownPrices());
    let isPaidMonthlyPlan = !isTrial && currentBillingPeriod === "monthly" && !isCurrentPlanFreePlan;
    setIsMonthly(isPaidMonthlyPlan);

    dispatch(switchIsMonthly(isPaidMonthlyPlan));
  }, [currentBillingPeriod, dispatch, isCurrentPlanFreePlan, isTrial]);

  const shouldShowSwitchToAnnually = useMemo(() => {
    return isMonthly && !isLifetime && !isTrial && !isCanceled && !isSuspended && !isFailedPayment;
  }, [isCanceled, isFailedPayment, isLifetime, isMonthly, isSuspended, isTrial]);

  const resolveAddOn = useMemo(() => {
    return isGetPaidEnabled || isGetPaidFullEnabled ? [isGetPaidFullEnabled ? "get_paid_full" : "get_paid"] : null;
  }, [isGetPaidEnabled, isGetPaidFullEnabled]);

  const onCancelSubscriptionLinkClick = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const renderCancelSubscriptionLink = useCallback(() => {
    if (!accountPlanInfo.is_cancelled || (accountPlanInfo.is_suspended && !accountPlanInfo.is_activated)) {
      return (
        <Body2
          color="text gray"
          title={cancelSubscriptionLinkText}
          className="tw-text-theme-600 tw-cursor-pointer tw-underline"
          onClick={onCancelSubscriptionLinkClick}
        >
          {cancelSubscriptionLinkText}
        </Body2>
      );
    }
  }, [
    accountPlanInfo.is_activated,
    accountPlanInfo.is_cancelled,
    accountPlanInfo.is_suspended,
    cancelSubscriptionLinkText,
    onCancelSubscriptionLinkClick,
  ]);

  const onCancelCrossClick = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const onGetAnswersLinkClick = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const renderComponent = useCallback(() => {
    if (isPerSeat && !isTrial && !isSuspendedTrial) {
      return (
        <div
          data-testid="perseat-plan-details"
          className="tw-grid tw-grid-cols-1 md:tw-grid-cols-[200px_auto] tw-grid-rows-7 tw-gap-2"
        >
          <BlockingPurchaseDialog
            open={isBlockingDialogOpen}
            onClose={closeBlockingDialog}
          />
          <PerSeatPlanDetails
            planInfo={accountPlanInfo}
            storageUsage={storageUsage}
            balanceTotal={balanceTotal}
            isCanceled={isCanceled}
          />
          <OrderSummaryDialog
            onUpdatingCreditCard={openUpdateCreditCard}
            onMissingPaymentMethod={openWizard}
            onCancel={closeActivateDialog}
            onConfirm={onSubmit}
            title="Upgrade to the Annual Plan"
            isLoading={isLoading}
            open={showActivateConfirmationModal}
            confirmBtnText="Activate"
          />
          <AddPaymentMethodProcess
            open={showAddPaymentMethodProcess}
            onClose={closeWizard}
            onSubmit={onSubmitWizard}
          />
          <AddStripeCardDialog
            open={showUpdateCreditCard}
            onClose={onCancelUpdateCreditCard}
            onSubmit={onUpdateCreditCard}
          />
          {renderCancelSubscriptionLink()}
        </div>
      );
    } else if (isLifetime && !isTrial) {
      return (
        <div
          data-testid="limited-plan-details"
          className="tw-grid tw-grid-cols-1 md:tw-grid-cols-[200px_auto] tw-grid-rows-7 tw-gap-2 tw-relative"
        >
          <LimitedPlanDetails
            planInfo={accountPlanInfo}
            storageUsage={storageUsage}
            balanceTotal={balanceTotal}
            isCurrentPlanFreePlan={isCurrentPlanFreePlan}
          />
          {accountPlanInfo?.name === "Free" ? renderCancelSubscriptionLink() : null}
        </div>
      );
    } else {
      return (
        <div
          data-testid="plan-details"
          className="tw-grid tw-grid-cols-1 md:tw-grid-cols-[200px_auto] tw-grid-rows-7 tw-gap-2 tw-relative"
        >
          <BuyNowButton
            url="/subscription/choose-plan"
            className="tw-absolute tw-right-0"
          />
          <PlanDetails
            planInfo={accountPlanInfo}
            storageUsage={storageUsage}
            balanceTotal={balanceTotal}
            isTrial={isTrial || isSuspendedTrial}
          />
          <OrderSummaryDialog
            onUpdatingCreditCard={openUpdateCreditCard}
            onMissingPaymentMethod={openWizard}
            onCancel={closeActivateDialog}
            onConfirm={onSubmit}
            title="Upgrade to the Annual Plan"
            isLoading={isLoading}
            open={showActivateConfirmationModal}
            confirmBtnText="Activate"
          />
          <AddPaymentMethodProcess
            open={showAddPaymentMethodProcess}
            onClose={closeWizard}
          />
          <AddStripeCardDialog
            open={showUpdateCreditCard}
            onClose={onCancelUpdateCreditCard}
            onSubmit={onUpdateCreditCard}
          />
          {renderCancelSubscriptionLink()}
        </div>
      );
    }
  }, [
    accountPlanInfo,
    balanceTotal,
    closeActivateDialog,
    closeBlockingDialog,
    closeWizard,
    isBlockingDialogOpen,
    isCanceled,
    isCurrentPlanFreePlan,
    isLifetime,
    isLoading,
    isPerSeat,
    isSuspendedTrial,
    isTrial,
    onCancelUpdateCreditCard,
    onSubmit,
    onSubmitWizard,
    onUpdateCreditCard,
    openUpdateCreditCard,
    openWizard,
    renderCancelSubscriptionLink,
    showActivateConfirmationModal,
    showAddPaymentMethodProcess,
    showUpdateCreditCard,
    storageUsage,
  ]);

  const onContinueCancellationProcessClick = useCallback(() => {
    setIsDialogOpen(false);
    setShowCancellationWizard(true);
  }, []);

  const handleOnCloseWizard = useCallback(() => {
    setShowCancellationWizard(false);
  }, []);

  const onCancelSubscriptionClick = useCallback(() => {
    setShowCancellationWizard(false);
  }, []);

  const shouldShowTryTheFreePlan = useMemo(
    () =>
      !isCurrentPlanFreePlan &&
      !isClassic &&
      ((isFreePlanAvailableForCancellation && !isFreePlanAvailableForActivation && isActivated) ||
        (isFreePlanAvailableForCancellation && isFreePlanAvailableForActivation)),
    [
      isActivated,
      isClassic,
      isCurrentPlanFreePlan,
      isFreePlanAvailableForActivation,
      isFreePlanAvailableForCancellation,
    ]
  );

  return (
    <>
      <div
        className={classnames("tw-flex tw-justify-between", {
          "tw-mb-1": isCurrentPlanAppSumoPlan,
        })}
      >
        <Header2
          color="primary"
          className="tw-pt-1"
        >
          Subscription
        </Header2>
        <div>
          {shouldShowSwitchToAnnually ? (
            <Button
              variant="text colored"
              className="tw-mb-1 tw-mr-4"
              onClick={switchToAnnually(resolveAddOn)}
            >
              Get Discount
            </Button>
          ) : null}
          {!isCurrentPlanAppSumoPlan && !(isTrial || isSuspendedTrial) && !isCurrentPlanFreePlan ? (
            <Button
              variant="text colored"
              className="tw-mb-1"
              disabled={isCanceled}
              onClick={handleChoosePlan}
            >
              Change Plan
            </Button>
          ) : null}
        </div>
      </div>
      <ListSeparator className="tw-mx-0 tw-mt-0 tw-mb-6" />
      {renderComponent()}
      <Dialog open={isDialogOpen}>
        <Dialog.Title
          disableDefaultHeading
          className="tw-flex tw-justify-between tw-items-center"
        >
          <Header2 weight="bold">
            {isTrial || isSuspendedTrial ? "Cancel Trial Account" : "Cancel Subscription"}
          </Header2>
          <IconButton
            onClick={onCancelCrossClick}
            variant="text gray"
          >
            <CancelCrossIcon />
          </IconButton>
        </Dialog.Title>
        <Dialog.ContentDivider />
        <Dialog.Content>
          {accountStats.projects > 0 ? (
            <>
              <div className="tw-flex tw-items-center">
                <div className="tw-mx-3">
                  <WarningTriangleIcon
                    fill="#ED6161"
                    className="tw-p-0"
                  />
                </div>
                <Body2
                  color="alert"
                  className="tw-ml-3"
                >
                  There are still {accountStats.projects} active projects, {accountStats.tasks} open tasks, and other
                  people on this account!
                </Body2>
              </div>
              <Dialog.ContentDivider className="tw--mx-[30px] tw-my-5" />
            </>
          ) : null}
          {shouldShowTryTheFreePlan ? (
            <ListItem
              className="tw-py-2 tw-px-0 tw-my-0"
              onClick={submitConfirmationModal}
            >
              <div className="tw-flex tw-items-center">
                <div className="tw-px-3 tw-mr-3 tw-flex tw-items-center">
                  <InvoicesIcon />
                </div>
                <div className="tw-flex tw-flex-col">
                  <Body2 weight="bold">Try the Free Plan</Body2>
                  <Caption1>Stay organized with ActiveCollab’s free version!</Caption1>
                </div>
              </div>
            </ListItem>
          ) : null}
          <ListItem className="tw-px-0">
            <a
              href={helpUrl}
              className="tw-no-underline tw-py-2 tw-w-full"
              rel="noreferrer"
              target="_blank"
              onClick={onGetAnswersLinkClick}
            >
              <div className="tw-flex tw-items-center">
                <div className="tw-px-3 tw-mr-3 tw-flex tw-items-center">
                  <HelpIcon />
                </div>
                <div className="tw-flex tw-flex-col">
                  <Body2 weight="bold">Read Guides</Body2>
                  <Caption1>Find out how to best use ActiveCollab.</Caption1>
                </div>
              </div>
            </a>
          </ListItem>
          <ListItem
            className="tw-py-2 tw-px-0 tw-my-0"
            onClick={onContinueCancellationProcessClick}
          >
            <div className="tw-flex tw-items-center">
              <div className="tw-px-3 tw-mr-3 tw-flex tw-items-center">
                <InvoicesIcon />
              </div>
              <div className="tw-flex tw-flex-col">
                <Body2 weight="bold">Cancel {isTrial ? "Trial" : "Subscription"}</Body2>
                <Caption1>Proceed with the cancellation process.</Caption1>
              </div>
            </div>
          </ListItem>
        </Dialog.Content>
      </Dialog>
      {showConfirmationModal ? (
        <ActivateFreePlanDialog
          open={showConfirmationModal}
          onClose={closeConfirmationModal}
          freePlan={freePlan}
          isTrial={isTrial}
          freePlanSurveyAvailable={isFreePlanSurveyAvailable}
          setBuyingProcessSuccessPlan={setBuyingProcessSuccessPlan}
          setShowBuyingProcessSuccess={setShowBuyingProcessSuccess}
          setReload={setReloadOnAction}
          isFromCancellation
        />
      ) : null}
      <Wizard
        title="Cancel Subscription"
        open={showCancellationWizard}
        onClose={handleOnCloseWizard}
      >
        <Step
          subtitle="Your Feedback"
          shouldRenderNextButton={false}
        >
          <CancelSurvey />
        </Step>
        <Step
          subtitle="Confirm Password"
          shouldRenderNextButton={false}
        >
          <CancellationProcessSecondStep onClick={onCancelSubscriptionClick} />
        </Step>
      </Wizard>
      <CompletingProcessSuccessDialog
        open={showBuyingProcessSuccess}
        title={buyingProcessSuccessPlan}
        reloadOnAction={reloadOnAction}
      />
    </>
  );
};

SubscriptionPlanDetails.displayName = `Subscription`;

export default SubscriptionPlanDetails;
